import React from "react";
import classes from "./DashboardCompositionTableStyles.module.scss";
import { t, Trans } from "@lingui/macro";
import { bigNumberify, formatAmount, formatKeyAmount } from "../../lib/numbers";
import { BASIS_POINTS_DIVISOR, DEFAULT_MAX_USDG_AMOUNT, importImage, USD_DECIMALS } from "../../lib/legacy";
import TooltipComponent from "../Tooltip/Tooltip";
import StatsTooltipRow from "../StatsTooltip/StatsTooltipRow";
import { CompositionWeightText } from "../CompositionWeightText/CompositionWeightText";
import { uniqueId } from "lodash";
import { Wrapper } from "../Wrapper/Wrapper";
import { ReactComponent as LinkIcon } from "./assets/LinkIcon.svg";
import { useChainId } from "../../lib/chains";
import { ICONLINKS } from "../../config/tokens";
import Tooltip from "../Tooltip/Tooltip";

type Props = {
  visibleTokens: any;
  infoTokens: any;
  adjustedUsdgSupply: any;
  totalTokenWeights: any;
};

export const DashboardCompositionTable = ({
  visibleTokens,
  infoTokens,
  totalTokenWeights,
  adjustedUsdgSupply,
}: Props) => {
  const { chainId } = useChainId();

  const getTokenImage = (token) => {
    if (token.name === "Atom") {
      return token.imageUrl;
    }
    return importImage("ic_" + token.symbol.toLowerCase() + "_40.svg");
  };

  const getMaxUsdgAmount = (tokenInfo: any) => {
    if (tokenInfo.maxUsdgAmount && tokenInfo.maxUsdgAmount.gt(0)) {
      return tokenInfo.maxUsdgAmount;
    }
    return DEFAULT_MAX_USDG_AMOUNT;
  };

  const getUtilization = (tokenInfo: any) => {
    if (tokenInfo && tokenInfo.reservedAmount && tokenInfo.poolAmount && tokenInfo.poolAmount.gt(0)) {
      return tokenInfo.reservedAmount.mul(BASIS_POINTS_DIVISOR).div(tokenInfo.poolAmount);
    }
    return bigNumberify(0);
  };

  const getTokenLink = (assetSymbol: string) => {
    const { coingecko } = ICONLINKS[chainId][assetSymbol] || {};
    return coingecko;
  };

  return (
    <Wrapper className={classes.wrapper}>
      <div className={classes.tableTitle}>
        <Trans>CLP Index Composition</Trans>
      </div>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <div className={classes.tableHead}>
            <div className={classes.tableRow}>
              <div className={classes.tableColumn}>
                <span className={classes.tableTitleItem}>
                  <Trans>Token</Trans>
                </span>
              </div>
              <div className={classes.tableColumn}>
                <span className={classes.tableTitleItem}>
                  <Trans>Price</Trans>
                </span>
              </div>
              <div className={classes.tableColumn}>
                <span className={classes.tableTitleItem}>
                  <Trans>Pool</Trans>
                </span>
              </div>
              <div className={classes.tableColumn}>
                <span className={classes.tableTitleItem}>
                  <Trans>Weight</Trans>
                </span>
              </div>
              <div className={classes.tableColumn}>
                <span className={classes.tableTitleItem}>
                  <Trans>Utilization</Trans>
                </span>
              </div>
            </div>
          </div>
          <div className={classes.tableBody}>
            {visibleTokens.map((token) => (
              <div key={uniqueId(token.symbol)} className={classes.tableRow}>
                <div className={classes.tableColumn}>
                  <div className={classes.tokenSymbolWrapper}>
                    <img src={getTokenImage(token)} alt={token.symbol} className={classes.tokenImage} />
                    <span className={classes.tokenSymbol}>{token.name}</span>
                    <a href={getTokenLink(token.symbol)} target={"_blank"} className={classes.link} rel="noreferrer">
                      <LinkIcon />
                    </a>
                  </div>
                </div>
                <div className={classes.tableColumn}>
                  ${formatKeyAmount(infoTokens[token.address], "minPrice", USD_DECIMALS, 2, true)}
                </div>
                <div className={classes.tableColumn}>
                  <Tooltip
                    handle={`$${formatKeyAmount(infoTokens[token.address], "managedUsd", USD_DECIMALS, 0, true)}`}
                    position="right-bottom"
                    className="nowrap"
                    renderContent={() => (
                      <>
                        <StatsTooltipRow
                          label={t`Pool Amount`}
                          value={`${formatKeyAmount(
                            infoTokens[token.address],
                            "managedAmount",
                            token.decimals,
                            0,
                            true
                          )} ${token.symbol}`}
                          showDollar={false}
                        />
                        <StatsTooltipRow
                          label={t`Target Min Amount`}
                          value={`${formatKeyAmount(
                            infoTokens[token.address],
                            "bufferAmount",
                            token.decimals,
                            0,
                            true
                          )} ${token.symbol}`}
                          showDollar={false}
                        />
                        <StatsTooltipRow
                          label={t`Max ${infoTokens[token.address].symbol} Capacity`}
                          value={formatAmount(getMaxUsdgAmount(infoTokens[token.address]), 18, 0, true)}
                          showDollar={true}
                        />
                      </>
                    )}
                  />
                </div>
                <div className={classes.tableColumn}>
                  <CompositionWeightText
                    adjustedUsdgSupply={adjustedUsdgSupply}
                    tokenInfo={infoTokens[token.address]}
                    totalTokenWeights={totalTokenWeights}
                  />
                </div>
                <div className={classes.tableColumn}>
                  {formatAmount(getUtilization(infoTokens[token.address]), 2, 2, false)}%
                </div>
              </div>
            ))}
          </div>
        </table>
      </div>
    </Wrapper>
  );
};
